/**
 * ChannelDonut — donut SVG puro de distribuição de alcance por canal.
 *
 * Usa tokens de cor do sistema (zero hardcode exceto os 3 primeiros canais
 * que usam as cores da marca CROSS por posição).
 * Acessível: role="img" + aria-label descritivo.
 *
 * Degrade honesto:
 * - 1 canal: mostra o anel completo na cor do canal + legenda honesta.
 * - N > 1 canais: segmentos proporcionais com gap de 3px.
 */

interface ChannelItem {
  readonly name: string;
  readonly reach: number;
}

interface ChannelDonutProps {
  readonly channels: readonly ChannelItem[];
}

/** Cores sequenciais por índice de canal — tokens de marca */
const CHANNEL_COLORS = [
  '#3F2570', // purple (primary)
  '#0079DC', // blue (secondary)
  '#F9005C', // pink (tertiary)
  '#7A3A9E', // violeta
  '#00A67E', // verde
];

function getColor(index: number): string {
  return CHANNEL_COLORS[index % CHANNEL_COLORS.length] ?? '#7A7570';
}

/** Formata número de alcance de forma legível */
function fmtReach(n: number): string {
  if (n >= 1_000_000)
    return (n / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 2 }) + ' M';
  if (n >= 1_000) return (n / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' K';
  return n.toLocaleString('pt-BR');
}

export function ChannelDonut({ channels }: ChannelDonutProps): JSX.Element {
  const SIZE = 144;
  const CX = SIZE / 2;
  const CY = SIZE / 2;
  const STROKE_W = 20;
  const R = (SIZE - STROKE_W * 2) / 2;
  const CIRCUMFERENCE = 2 * Math.PI * R;
  const GAP = 3;

  const total = channels.reduce((s, c) => s + c.reach, 0);
  const totalLabel = fmtReach(total);

  // Calcula dasharray/dashoffset para cada segmento
  type SegmentCalc = {
    name: string;
    reach: number;
    color: string;
    dashLength: number;
    dashOffset: number;
    pct: number;
  };

  const computed: SegmentCalc[] = channels.map((ch, i) => {
    const frac = total > 0 ? ch.reach / total : 0;
    // Soma das fracoes anteriores (prefix sum) sem mutar variavel externa — React Compiler safe.
    const usedLength =
      channels.slice(0, i).reduce((s, c) => s + (total > 0 ? c.reach / total : 0), 0) *
      CIRCUMFERENCE;
    const dashLength = Math.max(0, frac * CIRCUMFERENCE - (channels.length > 1 ? GAP : 0));
    const dashOffset = -(usedLength - CIRCUMFERENCE / 4);
    return {
      name: ch.name,
      reach: ch.reach,
      color: getColor(i),
      dashLength,
      dashOffset,
      pct: Math.round(frac * 100),
    };
  });

  const ariaLabel = `Distribuicao por canal: ${channels.map((c, i) => `${c.name} ${computed[i]?.pct ?? 0}%`).join(', ')}`;

  return (
    <div className="channel-donut-wrap">
      {/* SVG donut */}
      <div className="channel-donut-svg-row">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          width={SIZE}
          height={SIZE}
          role="img"
          aria-label={ariaLabel}
          focusable="false"
        >
          {/* Trilha */}
          <circle
            cx={CX}
            cy={CY}
            r={R}
            fill="none"
            stroke="var(--md-surface-container-high)"
            strokeWidth={STROKE_W}
          />
          {/* Segmentos */}
          {computed.map((seg, idx) => (
            <circle
              key={seg.name}
              cx={CX}
              cy={CY}
              r={R}
              fill="none"
              stroke={seg.color}
              strokeWidth={STROKE_W}
              strokeDasharray={`${seg.dashLength.toFixed(2)} ${CIRCUMFERENCE.toFixed(2)}`}
              strokeDashoffset={seg.dashOffset.toFixed(2)}
              strokeLinecap="butt"
              style={{
                opacity: 0,
                animation: `donutSegFadeIn ${700 + idx * 100}ms cubic-bezier(0.05,0.7,0.1,1.0) ${200 + idx * 80}ms forwards`,
              }}
            />
          ))}
          {/* Centro: total */}
          <text
            x={CX}
            y={CY - 6}
            textAnchor="middle"
            fontFamily="'Space Grotesk', sans-serif"
            fontSize="20"
            fontWeight="700"
            fill="var(--md-on-surface)"
          >
            {totalLabel}
          </text>
          <text
            x={CX}
            y={CY + 10}
            textAnchor="middle"
            fontFamily="'Roboto Flex', sans-serif"
            fontSize="9"
            fill="var(--md-on-surface-variant)"
          >
            alcancadas
          </text>
        </svg>
      </div>

      {/* Legenda */}
      <div className="channel-legend" role="list">
        {computed.map((seg) => (
          <div key={seg.name} className="channel-leg-row" role="listitem">
            <div className="channel-dot" style={{ background: seg.color }} aria-hidden="true" />
            <div className="channel-leg-label">{seg.name}</div>
            <div className="channel-leg-pct" style={{ color: seg.color, fontWeight: 700 }}>
              {seg.pct}%
            </div>
            <div className="channel-leg-val">{fmtReach(seg.reach)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
